import { Component, OnInit, ViewChild, ElementRef, inject } from "@angular/core";
import { Observable, of, map, catchError, tap } from "rxjs";
import { SaveChangesOnDeactivate } from "src/app/guards/unsaved-changes.guard";
import {
  TaskDto,
  TasksClientProvider,
  TaskStatus,
  TasksUpdateProcessOrdersCommand,
} from "src/app/web-api/web-api";
import { ProcessingOrderUiState } from "./ProcessingOrderUiState";
import { CstDialogsService } from "src/app/common-components/dialogs/cst-dialogs.service";
import { CstDialogResult } from "src/app/common-components/dialogs/cst-dialog-enums";
import { AnlageGroupSelectComponent } from "src/app/anlage-api/components/anlage-group/anlage-group-select.comnponent";

@Component({
    templateUrl: "./planung-processing-order.component.html",
    styleUrls: ["./planung-processing-order.component.scss"],
    standalone: false
})
export class PlanungApiProcessingOrderComponent implements OnInit, SaveChangesOnDeactivate {
  private _client = inject(TasksClientProvider);
  private _dialogs = inject(CstDialogsService);

  @ViewChild("taskGrid") _taskGridRef: ElementRef;
  public get taskGrid(): HTMLDivElement {
    if (!this._taskGridRef) {
      return null;
    }
    return this._taskGridRef.nativeElement;
  }
  @ViewChild(AnlageGroupSelectComponent)
  anlageGroupSelect: AnlageGroupSelectComponent;

  public get displayBottomToolbar() {
    return this.taskGrid.offsetHeight > window.innerHeight / 2;
  }

  public hasChanges = false;
  public uiState = new ProcessingOrderUiState();
  public tasks: Array<TaskDto> = [];

  public ngOnInit(): void {
    if (this.uiState.anlageGroup) {
      this._ReloadTasks(this.uiState.anlageGroup, true);
    }
  }

  public OnSave() {
    if (!this.hasChanges) {
      return;
    }
    this._executeSave$().subscribe((success) => {
      if (success) {
        this._dialogs.Information("Änderungen wurden übernommen!");
      }
    });
  }

  public hasUnsavedChanges(): boolean {
    return this.hasChanges;
  }

  public canSaveChanges(): boolean {
    return true;
  }

  public saveChanges(): Observable<boolean> | Promise<boolean> | boolean {
    if (!this.hasChanges) {
      return true;
    }
    return this._executeSave$();
  }

  private _executeSave$(): Observable<boolean> {
    const command = new TasksUpdateProcessOrdersCommand();
    command.tasksProcessOrders = {};
    for (let i = 0; i < this.tasks.length; ++i) {
      command.tasksProcessOrders[this.tasks[i].id] = i + 1;
    }
    return this._client.updateProcessOrders(command).pipe(
      tap(() => {
        this._ClearCurrentStateInTempStorage();
        this._ReloadTasks(this.uiState.anlageGroup);
      }),
      map(() => true),
      catchError(() => of(false)),
    );
  }

  public OnReset() {
    if (!this.hasChanges) {
      return;
    }
    this._dialogs
      .AskYesNo("Änderungen wirklich verwerfen")
      .subscribe(result => {
        if (result !== CstDialogResult.Yes) {
          return;
        }
        this._ClearCurrentStateInTempStorage();
        this._ReloadTasks(this.uiState.anlageGroup);
      });
  }

  public OnDragEnd(_: any) {
    this._RegisterChanges();
  }

  public OnAnlageGroupSelected(newValue: string) {
    this.uiState.anlageGroup = newValue;
    if (!this.hasChanges) {
      this._ReloadTasks(this.uiState.anlageGroup);
      return;
    }
    this._dialogs
      .AskYesNo(
        "Es gibt noch ungespeicherte Änderungen.\nSollen diese nun gespeichert werden?",
      )
      .subscribe(result => {
        switch (result) {
          case CstDialogResult.Yes:
            this.OnSave();
            return;

          case CstDialogResult.No:
            this._ClearCurrentStateInTempStorage();
            this._ReloadTasks(this.uiState.anlageGroup);
            return;
        }
      });
  }

  private _ReloadTasks(anlGrp: string, reloadAfterInit: boolean = false) {
    this.hasChanges = false;
    if (!anlGrp) {
      this.tasks = null;
      return;
    }
    this._client
      .getAll(
        null,
        null,
        null,
        null,
        TaskStatus.None,
        "",
        true,
        false,
        false,
        false,
        [anlGrp],
        "",
        null,
        [],
        [],
        [],
        false,
        "",
        0,
        10000,
      )
      .subscribe(result => {
        this.tasks = result.data.sort(
          (a, b) =>
            (a.processingOrder || Number.MAX_VALUE) -
            (b.processingOrder || Number.MAX_VALUE),
        );
        if (reloadAfterInit) {
          this._ReloadCurrentStateFromTempStorage();
        }
      });
  }

  private _RegisterChanges() {
    this.hasChanges = true;
    this._SaveCurrentStateToTempStorage();
  }

  private _ClearCurrentStateInTempStorage() {
    sessionStorage.removeItem("ProcessingOrderCurrentState");
  }
  private _SaveCurrentStateToTempStorage() {
    const state = this.tasks.map((x, idx) => ({
      order: idx + 1,
      label: x.label,
      id: x.id,
    }));
    sessionStorage.setItem(
      "ProcessingOrderCurrentState",
      JSON.stringify(state),
    );
  }
  private _ReloadCurrentStateFromTempStorage() {
    const currStateAsString = sessionStorage.getItem(
      "ProcessingOrderCurrentState",
    );
    if (!currStateAsString) {
      return;
    }
    const state = JSON.parse(currStateAsString) as Array<{
      id: string;
      order: number;
    }>;
    for (const item of state) {
      const task = this.tasks.find(x => x.id === item.id);
      if (!task) {
        continue;
      }
      task.processingOrder = item.order;
    }
    this.tasks = this.tasks.sort(
      (a, b) =>
        (a.processingOrder || Number.MAX_VALUE) -
        (b.processingOrder || Number.MAX_VALUE),
    );
    this.hasChanges = true;
  }
}
